export default function SponsoredContentPricing() {
  const packages = [
    {
      icon: 'ri-article-line',
      name: 'Sponsored Blog Post',
      price: '$250 - $500',
      description: 'Professional editorial content featuring your business story',
      features: [
        '800-1,200 word article',
        'Professional writing & editing',
        'SEO optimization',
        'Featured images included',
        'Social media promotion',
        'Permanent placement on site',
        '"Sponsored" badge disclosure',
        'Analytics report included'
      ],
      color: 'teal',
      popular: false
    },
    {
      icon: 'ri-mail-star-line',
      name: 'Newsletter Sponsor',
      price: '$150 - $300',
      description: 'Featured placement in our weekly community newsletter',
      features: [
        'Sent to 5,000+ subscribers',
        'Top banner placement',
        'Logo & 100-word description',
        'Direct link to your website',
        'Mobile-optimized design',
        'Click-through tracking',
        'Delivered every Thursday',
        'One-week exclusive sponsor'
      ],
      color: 'amber',
      popular: true
    },
    {
      icon: 'ri-star-line',
      name: 'Homepage Spotlight',
      price: '$200 - $400/week',
      description: 'Premium visibility on our high-traffic homepage',
      features: [
        'Featured in Business Spotlight',
        'Large format with images',
        'Prominent homepage placement',
        'Average 10K+ weekly views',
        'Full business profile',
        'Customer reviews displayed',
        'Direct contact buttons',
        'Weekly performance report'
      ],
      color: 'violet',
      popular: false
    }
  ];

  const colorClasses = {
    teal: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      icon: 'text-teal-600',
      badge: 'bg-teal-600',
      button: 'bg-teal-600 hover:bg-teal-700'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'text-amber-600',
      badge: 'bg-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700'
    },
    violet: {
      bg: 'bg-violet-50',
      border: 'border-violet-200',
      icon: 'text-violet-600',
      badge: 'bg-violet-600',
      button: 'bg-violet-600 hover:bg-violet-700'
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full mb-4">
            <i className="ri-price-tag-3-line text-teal-600"></i>
            <span className="text-teal-700 text-sm font-semibold">Transparent Pricing</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Sponsored Content Packages
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose the perfect advertising solution to reach Roseville's engaged community
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {packages.map((pkg) => {
            const colors = colorClasses[pkg.color as keyof typeof colorClasses];
            return (
              <div
                key={pkg.name}
                className={`relative bg-white rounded-2xl border-2 ${colors.border} p-8 hover:shadow-xl transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-amber-100' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full shadow-lg whitespace-nowrap">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${pkg.icon} text-2xl ${colors.icon}`}></i>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-gray-900 mb-4">{pkg.price}</div>
                <p className="text-gray-600 text-sm mb-6">{pkg.description}</p>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className={`ri-checkbox-circle-fill ${colors.icon} text-lg mt-0.5 flex-shrink-0`}></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#sponsored-content-form"
                  className={`block w-full px-6 py-3 ${colors.button} text-white rounded-lg font-semibold text-center transition-colors whitespace-nowrap cursor-pointer`}
                >
                  Get Started
                </a>
              </div>
            );
          })}
        </div>

        {/* Custom Package CTA */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-10 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-vip-crown-line text-3xl text-amber-400"></i>
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">
              Need a Custom Package?
            </h3>
            <p className="text-gray-300 text-lg mb-8">
              Combine multiple placements for maximum impact. We'll create a tailored advertising strategy that fits your goals and budget.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#sponsored-content-form"
                className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Custom Quote
              </a>
              <a
                href="mailto:advertise@roseville.org"
                className="px-8 py-3 bg-white/10 border border-white/30 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                Contact Sales Team
              </a>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { icon: 'ri-eye-line', value: '50K+', label: 'Monthly Visitors' },
            { icon: 'ri-mail-line', value: '5K+', label: 'Newsletter Subscribers' },
            { icon: 'ri-time-line', value: '3.5 min', label: 'Avg. Time on Site' },
            { icon: 'ri-user-heart-line', value: '68%', label: 'Return Visitors' }
          ].map((stat, idx) => (
            <div key={idx} className="text-center p-6 bg-white rounded-xl border border-gray-200">
              <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-xl text-teal-600`}></i>
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}